import { IonContent, IonPage } from '@ionic/react';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import PageTitle from '../../../../components/Dashboard/PageTitle';
import { dashboardPageStyle } from '../../Layout';
import { RootState } from '../../../../store/store';
import {
  EarningsRange,
  getStoreEarnings,
} from '../../../../services/inventory/getStoreEarnings';
import { formatPrice } from '../../../../utils/formatPrice';
import EmptyPage from '../../../../components/ui/EmptyPage';

const RANGE_OPTIONS: { value: EarningsRange; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];

function shortDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function compactNumber(n: number) {
  if (Math.abs(n) >= 1_000_000)
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (Math.abs(n) >= 1_000)
    return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

export default function index() {
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const { activeStore } = useSelector((state: RootState) => state.userStores);
  const [range, setRange] = useState<EarningsRange>('30d');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['store-earnings', activeStore?.id, range],
    enabled: Boolean(authToken) && Boolean(activeStore?.id),
    queryFn: () =>
      getStoreEarnings(activeStore!.id, authToken as string, range),
  });

  const feePercent = data ? Math.round(data.fee_rate * 100) : 10;

  const chartData = React.useMemo(
    () =>
      (data?.by_day ?? []).map((p) => ({
        date: p.date,
        label: shortDate(p.date),
        gross: Number(p.gross),
        net: Number(p.net),
        orders: p.orders,
      })),
    [data]
  );
  const chartEmpty =
    chartData.length === 0 || chartData.every((p) => p.gross === 0);

  if (!activeStore?.id) {
    return (
      <IonPage>
        <IonContent>
          <div className={dashboardPageStyle}>
            <PageTitle>Earnings</PageTitle>
            <EmptyPage message="Pick or create a store first." />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <div className={dashboardPageStyle}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <PageTitle>Earnings</PageTitle>
              <p className="mt-1 text-sm text-text-dark-medium">
                You receive {100 - feePercent}% of every sale (Swiftmedix keeps{' '}
                {feePercent}% per the platform terms).
              </p>
            </div>
            <div className="inline-flex rounded border border-border bg-white p-1 text-sm">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRange(opt.value)}
                  className={`rounded px-3 py-1.5 transition-colors ${
                    range === opt.value
                      ? 'bg-primary text-white'
                      : 'text-text-dark-medium hover:bg-customs-lavender'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {isError && (
            <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">
              Failed to load earnings. Refresh to retry.
            </div>
          )}

          {/* Top tiles */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              title="Net earned"
              subtitle={
                data
                  ? `Lifetime · gross ${formatPrice(Number(data.lifetime_gross))}`
                  : '—'
              }
              value={
                isLoading
                  ? '…'
                  : data
                    ? formatPrice(Number(data.lifetime_net))
                    : '—'
              }
              tone="emerald"
            />
            <Tile
              title="Pending payout"
              subtitle="Money owed (delivered, not yet settled)"
              value={
                isLoading
                  ? '…'
                  : data
                    ? formatPrice(Number(data.pending_payout))
                    : '—'
              }
              tone="amber"
            />
            <Tile
              title="Settled"
              subtitle="Already paid out"
              value={
                isLoading
                  ? '…'
                  : data
                    ? formatPrice(Number(data.settled_amount))
                    : '—'
              }
              tone="blue"
            />
            <Tile
              title={`Range · ${RANGE_OPTIONS.find((r) => r.value === range)?.label ?? range}`}
              subtitle={
                data
                  ? `${data.range_orders} orders · gross ${formatPrice(Number(data.range_gross))}`
                  : '—'
              }
              value={
                isLoading
                  ? '…'
                  : data
                    ? formatPrice(Number(data.range_net))
                    : '—'
              }
              tone="violet"
            />
          </div>

          {/* Trend */}
          <section className="rounded border border-border bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-dark-medium">
                Cash & orders trend
              </h2>
              {!chartEmpty && (
                <div className="flex items-center gap-3 text-xs text-text-dark-medium">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" />
                    Net cash
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-blue-500" />
                    Orders
                  </span>
                </div>
              )}
            </div>
            <div className="mt-4 h-72">
              {isLoading ? (
                <div className="text-sm text-text-dark-medium">Loading…</div>
              ) : chartEmpty ? (
                <div className="text-sm text-text-dark-medium">
                  No earnings in the selected range yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                  >
                    <defs>
                      <linearGradient id="netFill" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="0%"
                          stopColor="#10b981"
                          stopOpacity={0.35}
                        />
                        <stop
                          offset="100%"
                          stopColor="#10b981"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                      minTickGap={20}
                    />
                    <YAxis
                      yAxisId="cash"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => compactNumber(v)}
                    />
                    <YAxis
                      yAxisId="orders"
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === 'net')
                          return [formatPrice(Number(value)), 'Net cash'];
                        return [String(value), 'Orders'];
                      }}
                      labelFormatter={(label) => {
                        const point = chartData.find((p) => p.label === label);
                        return point
                          ? new Date(point.date).toLocaleDateString()
                          : String(label);
                      }}
                    />
                    <Area
                      yAxisId="cash"
                      type="monotone"
                      dataKey="net"
                      stroke="#10b981"
                      strokeWidth={2}
                      fill="url(#netFill)"
                    />
                    <Bar
                      yAxisId="orders"
                      dataKey="orders"
                      fill="#3b82f6"
                      fillOpacity={0.7}
                      barSize={
                        chartData.length > 60
                          ? 4
                          : chartData.length > 30
                            ? 6
                            : 12
                      }
                      radius={[3, 3, 0, 0]}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>
        </div>
      </IonContent>
    </IonPage>
  );
}

interface TileProps {
  title: string;
  subtitle?: string;
  value: string;
  tone: 'emerald' | 'blue' | 'violet' | 'amber';
}
const TONE: Record<TileProps['tone'], string> = {
  emerald: 'border-emerald-100 bg-emerald-50',
  blue: 'border-blue-100 bg-blue-50',
  violet: 'border-violet-100 bg-violet-50',
  amber: 'border-amber-100 bg-amber-50',
};
function Tile({ title, subtitle, value, tone }: TileProps) {
  return (
    <div className={`rounded border bg-white p-5 shadow-sm ${TONE[tone]}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-text-dark-medium">
        {title}
      </div>
      <div className="mt-2 text-2xl font-semibold text-text-dark-high">
        {value}
      </div>
      {subtitle && (
        <div className="mt-1 text-xs text-text-dark-medium">{subtitle}</div>
      )}
    </div>
  );
}
